import React, { useState, useEffect } from "react";
import { TextField, Button, Box, Typography, Alert } from "@mui/material";
import { db } from "../services/firebase";
import { collection, query, where, getDocs, doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { auth } from "../services/firebase";
import { useNavigate } from "react-router-dom";
import { nanoid } from "nanoid";
import { useAuth } from "../contexts/AuthContext"; // AuthContextからログイン情報を取得

const UserSetupPage: React.FC = () => {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const { user, loading } = useAuth(); // ログイン状態を取得
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return; // ローディング中は何もしない

    if (!user) {
      // 未ログインの場合は/authにリダイレクト
      navigate("/auth");
      return;
    }

    // データベースを確認し、すでにユーザー情報が存在する場合はダッシュボードにリダイレクト
    const checkUserInDatabase = async () => {
      try {
        // authUidを基に検索
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("authUid", "==", user.uid));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          navigate("/dashboard"); // データが存在すればダッシュボードへ
        }
      } catch (error) {
        console.error("ユーザーデータの確認中にエラーが発生しました:", error);
        setError("サーバーエラーが発生しました。後でもう一度お試しください。");
      }
    };

    checkUserInDatabase();
  }, [user, loading, navigate]);

  const generateUniqueNanoid = async (): Promise<string> => {
    while (true) {
      const id = nanoid(15);
      const docRef = doc(db, "users", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return id;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError("認証エラーが発生しました。再度ログインしてください。");
      return;
    }

    if (name.trim().length === 0) {
      setError("ユーザー名を入力してください。");
      return;
    }

    if (name.length > 50) {
      setError("ユーザー名は50文字以内で入力してください。");
      return;
    }

    try {
      const userUid = await generateUniqueNanoid();
      const userDocRef = doc(db, "users", userUid);

      await setDoc(userDocRef, {
        authUid: user.uid,
        name: name.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      navigate("/dashboard"); // 情報入力後にダッシュボードへリダイレクト
    } catch (error) {
      setError("ユーザー情報の登録中にエラーが発生しました。");
      console.error(error);
    }
  };

  if (loading) {
    return <p>読み込み中...</p>; // ローディング中の表示
  }

  return (
    <Box
      sx={{
        maxWidth: 400,
        margin: "auto",
        mt: 8,
        p: 3,
        borderRadius: 1,
        boxShadow: 3,
        backgroundColor: "white",
      }}
    >
      <Typography variant="h5" component="h1" textAlign="center" gutterBottom>
        初期登録
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: "center" }}>
        ユーザー名を入力して初期設定を完了してください。
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="ユーザー名"
          value={name}
          onChange={(e) => setName(e.target.value)}
          margin="normal"
          required
          inputProps={{ maxLength: 50 }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2 }}
        >
          保存
        </Button>
      </form>
    </Box>
  );
};

export default UserSetupPage;
